import CreditCardIcon from '@mui/icons-material/CreditCard';
import DialpadIcon from '@mui/icons-material/Dialpad';
import MeetingRoom from '@mui/icons-material/MeetingRoom';
import { Box, Stack, SvgIcon, Tooltip } from '@mui/material';
import { useIntl } from 'react-intl';
import { ReactComponent as TimeLimitedIcon } from '../../assets/time-limited.svg';
import featureFlags from '../../config/featureFlags';
import { UserCredentials } from '../../modules/commonTypes';
import My2NAppAccessDisabledIcon from '../_Icons/My2NAppAccessDisabledIcon';
import My2NAppAccessIcon from '../_Icons/My2NAppAccessIcon';
import { useHasFeature } from '../Feature/Feature';
import messages from '../SiteUsersDataGrid/messages';
import { getStatusTooltipTitle } from './statusTooltipTitle';
import { getVisibilityOfStatuses } from './statusVisibility';

export interface IUserAccessSettingsStatusProps {
  apartmentAccess?: boolean;
  credentials: UserCredentials[];
  hiddenStatuses?: UserCredentials[];
  isSiteBleActive?: boolean;
  isTimeLimitedAccess?: boolean;
}

const UserAccessSettingsStatus = ({
  apartmentAccess,
  credentials,
  hiddenStatuses,
  isSiteBleActive,
  isTimeLimitedAccess,
}: IUserAccessSettingsStatusProps) => {
  const isUserBleCredentialFFEnable = useHasFeature(featureFlags.USER_BLE_CREDENTIAL);
  const { isBTStatusVisible, isPinStatusVisible, isRFIDStatusVisible } = getVisibilityOfStatuses({
    hiddenStatuses,
    isUserBleCredentialFFEnable,
  });

  const { formatMessage } = useIntl();

  const isServiceActive = (service: UserCredentials) => credentials?.includes(service);
  const getIconColor = (isServiceActive: boolean) => (isServiceActive ? 'primary' : 'disabled');

  const isPinActive = isPinStatusVisible && isServiceActive(UserCredentials.PIN);
  const isRFIDActive = isRFIDStatusVisible && isServiceActive(UserCredentials.RFID);
  const isBTActive = isBTStatusVisible && isServiceActive(UserCredentials.BT);

  const tooltipTitleValues = { br: <br /> };

  return (
    <Stack direction="row" sx={{ alignItems: 'center', fontSize: 0, gap: 1 }}>
      {isPinStatusVisible && (
        <Tooltip title={formatMessage(getStatusTooltipTitle(isPinActive, UserCredentials.PIN), tooltipTitleValues)}>
          <DialpadIcon color={getIconColor(isPinActive)} />
        </Tooltip>
      )}
      {isRFIDStatusVisible && (
        <Tooltip title={formatMessage(getStatusTooltipTitle(isRFIDActive, UserCredentials.RFID), tooltipTitleValues)}>
          <CreditCardIcon color={getIconColor(isRFIDActive)} />
        </Tooltip>
      )}
      {isSiteBleActive && isBTStatusVisible && (
        <Tooltip title={formatMessage(getStatusTooltipTitle(isBTActive, UserCredentials.BT), tooltipTitleValues)}>
          <Box sx={{ display: 'inherit' }}>
            <My2NAppAccessIcon color={getIconColor(isBTActive)} />
          </Box>
        </Tooltip>
      )}
      {!isSiteBleActive && (
        <Tooltip title={formatMessage(messages.userAccessSettingsStatusBluetoothDisabled, { br: <br /> })}>
          <Box sx={{ display: 'inherit' }}>
            <My2NAppAccessDisabledIcon color="disabled" />
          </Box>
        </Tooltip>
      )}
      {isTimeLimitedAccess && (
        <Tooltip placement="right" title={formatMessage(messages.UserListUserGuestIconTooltip)}>
          <Box sx={{ display: 'inherit' }}>
            <SvgIcon sx={{ ml: 1 }} component={TimeLimitedIcon} color="primary" />
          </Box>
        </Tooltip>
      )}
      {apartmentAccess && (
        <Tooltip placement="right" title={formatMessage(messages.UserListUserGuestIcon2Tooltip)}>
          <Box sx={{ display: 'inherit' }}>
            <MeetingRoom color="primary" />
          </Box>
        </Tooltip>
      )}
    </Stack>
  );
};

export default UserAccessSettingsStatus;
